import { useState, useRef, useCallback, useEffect } from 'react'
import { X, Search } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../config/api'
import { inputClass } from '../styles/forms'

interface LeaderOption {
  id: number
  name: string
  nickname?: string | null
  city_name?: string | null
  state_code?: string | null
}

interface LeaderSelectProps {
  label?: string
  value: string
  onChange: (leaderId: string, leader?: LeaderOption) => void
  /** Nome inicial (quando o valor vem preenchido de fora, ex: edição) */
  initialName?: string
  placeholder?: string
  dropdownZIndex?: number
  /** Lideranças a esconder da lista (ex: a própria, ao escolher pai no organograma) */
  excludeIds?: number[]
}

/**
 * Typeahead de liderança com busca no servidor (paginada) — encontra qualquer
 * liderança independente da quantidade. Substitui os dropdowns que carregavam
 * só as primeiras N.
 */
export default function LeaderSelect({ label = 'Liderança', value, onChange, initialName = '', placeholder = 'Buscar liderança por nome...', dropdownZIndex = 100003, excludeIds = [] }: LeaderSelectProps) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<LeaderOption[]>([])
  const [selectedName, setSelectedName] = useState(initialName)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mantém o nome exibido em sincronia quando o valor/nome vem de fora (edição)
  useEffect(() => { setSelectedName(initialName) }, [initialName])

  const fetchLeaders = useCallback(async (q: string, p: number, append = false) => {
    setLoading(true)
    try {
      const params: any = { per_page: 15, page: p }
      if (q.length >= 2) params.search = q
      const res = await axios.get(buildApiUrl('/v1/leaders'), { params })
      const data: LeaderOption[] = res.data.data
        .map((l: any) => ({ id: l.id, name: l.name, nickname: l.nickname, city_name: l.city_name, state_code: l.state_code }))
        .filter((l: LeaderOption) => !excludeIds.includes(l.id))
      setResults(prev => append ? [...prev, ...data] : data)
      setTotal(res.data.meta?.total || 0)
      setHasMore(p < (res.data.meta?.last_page || 1))
    } catch { /* */ } finally { setLoading(false) }
  }, [excludeIds])

  function handleSearch(q: string) {
    setSearch(q); setPage(1)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchLeaders(q, 1), 300)
  }

  function handleFocus() {
    if (results.length === 0) fetchLeaders('', 1)
    setDropdownOpen(true)
  }

  function handleScroll() {
    const el = scrollRef.current
    if (!el || loading || !hasMore) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      const next = page + 1; setPage(next); fetchLeaders(search, next, true)
    }
  }

  function handleSelect(l: LeaderOption) {
    onChange(String(l.id), l); setSelectedName(l.name); setSearch(''); setDropdownOpen(false)
  }
  function handleClear() { onChange(''); setSelectedName(''); setSearch('') }

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}
      <div className="relative">
        <input
          value={value ? selectedName : search}
          onChange={e => { if (value) handleClear(); handleSearch(e.target.value); setDropdownOpen(true) }}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={inputClass}
          readOnly={!!value}
        />
        {value ? (
          <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        ) : (
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
        )}

        {dropdownOpen && !value && (
          <>
            <div className="fixed inset-0" style={{ zIndex: dropdownZIndex - 1 }} onClick={() => setDropdownOpen(false)} />
            <div ref={scrollRef} onScroll={handleScroll}
              className="absolute left-0 right-0 top-12 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
              style={{ zIndex: dropdownZIndex }}>
              {results.length === 0 && !loading ? (
                <p className="px-4 py-3 text-sm text-gray-400 text-center">{search.length >= 2 ? 'Nenhuma liderança encontrada' : 'Digite para buscar'}</p>
              ) : (
                results.map(l => (
                  <button type="button" key={l.id} onClick={() => handleSelect(l)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-sm text-gray-900">{l.name}</span>
                        {l.nickname && <span className="text-xs text-gray-400 ml-2">({l.nickname})</span>}
                      </div>
                      {l.city_name && <span className="text-xs text-gray-400 flex-shrink-0">{l.city_name}{l.state_code ? `/${l.state_code}` : ''}</span>}
                    </div>
                  </button>
                ))
              )}
              {loading && (
                <div className="py-2 text-center"><div className="w-5 h-5 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto" /></div>
              )}
              {results.length > 0 && (
                <div className="px-4 py-2 text-[10px] text-gray-400 text-center border-t border-gray-100">{results.length} de {total} lideranças</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
